import React, { useState } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box, 
  Menu, 
  MenuItem, 
  IconButton,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
  Avatar,
  Tooltip,
  FormControlLabel
} from '@mui/material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useThemeContext, useToggleTheme } from '../context/ThemeContext';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import ScienceIcon from '@mui/icons-material/Science';
import ArticleIcon from '@mui/icons-material/Article';
import ApiIcon from '@mui/icons-material/Api';
import LockIcon from '@mui/icons-material/Lock';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import LogoutIcon from '@mui/icons-material/Logout';
import ChatIcon from '@mui/icons-material/Chat';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import DescriptionIcon from '@mui/icons-material/Description';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ChangePasswordDialog from './ChangePasswordDialog';
import { ChatNotificationBadge } from './Chat/index';

const NavBar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const muiTheme = useTheme();
  const { theme } = useThemeContext();
  const { toggleTheme } = useToggleTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const { logout, user } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    setDrawerOpen(false);
  };

  const handleUserManagement = () => {
    handleClose();
    navigate('/users');
    setDrawerOpen(false);
  };

  const handleBusinessSettings = () => {
    handleClose();
    navigate('/business-settings');
    setDrawerOpen(false);
  };

  const handleNotificationSettings = () => {
    handleClose();
    navigate('/notification-settings');
    setDrawerOpen(false);
  };
  
  const handleAdminPanel = () => {
    handleClose();
    navigate('/admin');
    setDrawerOpen(false);
  };

  const handleChangePassword = () => {
    handleClose();
    setPasswordDialogOpen(true);
    setDrawerOpen(false);
  };

  const handlePasswordSuccess = () => {
    // You could show a success message here if needed
  };

  const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    setDrawerOpen(false);
  };

  // Balanced theme header style with gradient
  const headerGradient = 'linear-gradient(135deg, #546BDA 0%, #6B7FE3 100%)';
  
  return (
    <>
      <AppBar 
        position="static" 
        sx={{ 
          background: headerGradient,
          boxShadow: '0 2px 10px rgba(47, 58, 77, 0.15)'
        }}
      >
        <Toolbar>
          {/* Logo and title */}
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
            <Box
              sx={{
                display: { xs: 'none', sm: 'flex' },
                alignItems: 'center',
                justifyContent: 'center',
                width: 48,
                height: 48,
                mr: 1,
              }}
            >
              <img
                src="/images/hydra.png"
                alt="Logo"
                style={{
                  width: '100%',
                  height: 'auto',
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                }}
              />
            </Box>
            <Typography 
              variant="h6" 
              component="div" 
              sx={{ 
                flexGrow: 1,
                fontWeight: '600',
                fontSize: '1.25rem',
                color: '#FFFFFF',
                textShadow: '0 1px 2px rgba(47, 58, 77, 0.1)',
                letterSpacing: '-0.01em',
              }}
            >
              Beauty Emergency Clinic Portal
            </Typography>
          </Box>

          {/* Mobile menu button */}
          {isMobile && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={toggleDrawer(true)}
              size="medium"
              sx={{ 
                mr: 1,
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                },
                width: 40,
                height: 40,
                borderRadius: '8px',
              }}
            >
              <MenuIcon sx={{ color: '#FFFFFF' }} fontSize="medium" />
            </IconButton>
          )}
          
          {/* Desktop navigation buttons */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button 
                color="inherit" 
                component={Link} 
                to="/"
                sx={{ 
                  mx: 0.75,
                  fontWeight: 500,
                  borderRadius: '8px',
                  minHeight: '36px',
                  padding: '6px 16px',
                  color: '#FFFFFF',
                  ...(location.pathname === '/' && {
                    position: 'relative',
                    fontWeight: 600,
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -1,
                      left: '10%',
                      width: '80%',
                      height: '3px',
                      backgroundColor: '#FFFFFF',
                      borderRadius: '2px',
                    },
                  }),
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                <HomeIcon 
                  sx={{ 
                    mr: 0.75, 
                    color: '#FFFFFF'
                  }} 
                  fontSize="small" 
                />
                Dashboard
              </Button>
              <Button 
                color="inherit" 
                component={Link} 
                to="/documents"
                sx={{ 
                  mx: 0.75,
                  fontWeight: 500,
                  borderRadius: '8px',
                  minHeight: '36px',
                  padding: '6px 16px',
                  color: '#FFFFFF',
                  ...(location.pathname === '/documents' && {
                    position: 'relative',
                    fontWeight: 600,
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -1,
                      left: '10%',
                      width: '80%',
                      height: '3px',
                      backgroundColor: '#FFFFFF',
                      borderRadius: '2px',
                    },
                  }),
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                <FolderSharedIcon 
                  sx={{ 
                    mr: 0.75, 
                    color: '#FFFFFF'
                  }} 
                  fontSize="small" 
                />
                Documents
              </Button>
              <Button 
                color="inherit" 
                component={Link} 
                to="/chat"
                sx={{ 
                  mx: 0.75,
                  fontWeight: 500,
                  borderRadius: '8px',
                  minHeight: '36px',
                  padding: '6px 16px',
                  color: '#FFFFFF',
                  ...(location.pathname === '/chat' && {
                    position: 'relative',
                    fontWeight: 600,
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -1,
                      left: '10%',
                      width: '80%',
                      height: '3px',
                      backgroundColor: '#FFFFFF',
                      borderRadius: '2px',
                    },
                  }),
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 0.75, color: '#FFFFFF' }}>
                  <ChatNotificationBadge />
                </Box>
                Chat
              </Button>
              <Button 
                color="inherit" 
                component={Link} 
                to="/hydra-chat"
                sx={{ 
                  mx: 0.75,
                  fontWeight: 500,
                  borderRadius: '8px',
                  minHeight: '36px',
                  padding: '6px 16px',
                  color: '#FFFFFF',
                  ...(location.pathname === '/hydra-chat' && {
                    position: 'relative',
                    fontWeight: 600,
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -1,
                      left: '10%',
                      width: '80%',
                      height: '3px',
                      backgroundColor: '#FFFFFF',
                      borderRadius: '2px',
                    },
                  }),
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                <SmartToyIcon 
                  sx={{ 
                    mr: 0.75, 
                    color: '#FFFFFF'
                  }} 
                  fontSize="small" 
                />
                AI Chat
              </Button>
              <Button 
                color="inherit" 
                component={Link} 
                to="/request-center"
                sx={{ 
                  mx: 0.75,
                  fontWeight: 500,
                  borderRadius: '8px',
                  minHeight: '36px',
                  padding: '6px 16px',
                  color: '#FFFFFF',
                  ...(location.pathname === '/request-center' && {
                    position: 'relative',
                    fontWeight: 600,
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -1,
                      left: '10%',
                      width: '80%',
                      height: '3px',
                      backgroundColor: '#FFFFFF',
                      borderRadius: '2px',
                    },
                  }),
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                <DescriptionIcon 
                  sx={{ 
                    mr: 0.75, 
                    color: '#FFFFFF'
                  }} 
                  fontSize="small" 
                />
                Request Center
              </Button>
            </Box>
          )}
          
          <Box sx={{ flexGrow: 1 }} />

          {/* User menu */}
          <Box>
            <Tooltip title="Account settings">
              <IconButton
                size="medium"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
                sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                  },
                  width: 40,
                  height: 40,
                  borderRadius: '8px',
                }}
              >
                <AccountCircleIcon sx={{ color: '#FFFFFF' }} fontSize="medium" />
              </IconButton>
            </Tooltip>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              PaperProps={{
                sx: {
                  mt: 1.5,
                  borderRadius: '3px',
                  boxShadow: '0 4px 8px rgba(9, 30, 66, 0.25)',
                  border: '1px solid #DFE1E6',
                  overflow: 'hidden'
                }
              }}
            >
              <Box sx={{ px: 3, py: 2, backgroundColor: '#F4F5F7', borderBottom: '1px solid #DFE1E6' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: '600', color: '#172B4D', mb: 0.5 }}>
                  {user?.username || 'User'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#5E6C84', fontSize: '0.8125rem' }}>
                  {user?.id || 'ID not available'}
                </Typography>
              </Box>
              <Divider sx={{ my: 0 }} />
              
              {user && user.role === 'admin' && (
                <>
                  <MenuItem onClick={handleUserManagement} sx={{ py: 1.75, mx: 1, my: 0.5, borderRadius: '8px', '&:hover': { backgroundColor: 'rgba(2, 102, 204, 0.05)' } }}>
                    <ListItemIcon>
                      <PeopleIcon fontSize="small" sx={{ color: '#0266cc' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="User Management" 
                      primaryTypographyProps={{ 
                        fontWeight: 600, 
                        fontSize: '0.95rem',
                        color: '#2a3548'
                      }} 
                    />
                  </MenuItem>
                  <MenuItem onClick={handleBusinessSettings} sx={{ py: 1.75, mx: 1, my: 0.5, borderRadius: '8px', '&:hover': { backgroundColor: 'rgba(2, 102, 204, 0.05)' } }}>
                    <ListItemIcon>
                      <BusinessIcon fontSize="small" sx={{ color: '#0266cc' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Business Settings" 
                      primaryTypographyProps={{ 
                        fontWeight: 600, 
                        fontSize: '0.95rem',
                        color: '#2a3548'
                      }} 
                    />
                  </MenuItem>
                  <MenuItem onClick={handleNotificationSettings} sx={{ py: 1.75, mx: 1, my: 0.5, borderRadius: '8px', '&:hover': { backgroundColor: 'rgba(2, 102, 204, 0.05)' } }}>
                    <ListItemIcon>
                      <NotificationsIcon fontSize="small" sx={{ color: '#009999' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Notification Settings" 
                      primaryTypographyProps={{ 
                        fontWeight: 600, 
                        fontSize: '0.95rem',
                        color: '#2a3548'
                      }} 
                    />
                  </MenuItem>
                  <MenuItem onClick={handleAdminPanel} sx={{ py: 1.75, mx: 1, my: 0.5, borderRadius: '8px', '&:hover': { backgroundColor: 'rgba(2, 102, 204, 0.05)' } }}>
                    <ListItemIcon>
                      <AdminPanelSettingsIcon fontSize="small" sx={{ color: '#0266cc' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Admin Panel" 
                      primaryTypographyProps={{ 
                        fontWeight: 600, 
                        fontSize: '0.95rem',
                        color: '#2a3548'
                      }} 
                    />
                  </MenuItem>
                  <Divider sx={{ my: 1.5, mx: 2, borderColor: 'rgba(86, 106, 135, 0.12)' }} />
                </>
              )}
              
              <MenuItem onClick={handleChangePassword} sx={{ py: 1.75, mx: 1, my: 0.5, borderRadius: '8px', '&:hover': { backgroundColor: 'rgba(2, 102, 204, 0.05)' } }}>
                <ListItemIcon>
                  <LockIcon fontSize="small" sx={{ color: '#009999' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Change Password" 
                  primaryTypographyProps={{ 
                    fontWeight: 600, 
                    fontSize: '0.95rem',
                    color: '#2a3548'
                  }} 
                />
              </MenuItem>
              
              <Divider sx={{ my: 1.5, mx: 2, borderColor: 'rgba(86, 106, 135, 0.12)' }} />
              
              <MenuItem onClick={handleLogout} sx={{ py: 1.75, mx: 1, my: 0.5, borderRadius: '8px', '&:hover': { backgroundColor: 'rgba(230, 57, 70, 0.05)' } }}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" sx={{ color: '#e63946' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Logout" 
                  primaryTypographyProps={{ 
                    fontWeight: 600, 
                    fontSize: '0.95rem',
                    color: '#e63946'
                  }} 
                />
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            width: 280,
            bgcolor: '#FFFFFF',
            boxShadow: '0 4px 12px rgba(9, 30, 66, 0.15)',
            borderRight: '1px solid #DFE1E6'
          }
        }}
      >
        <Box
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 1,
              width: 120,
              height: 120,
            }}
          >
            <img
              src="/images/hydra.png"
              alt="Logo"
              style={{
                width: '100%',
                height: 'auto',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
              }}
            />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1, mt: 1 }}>
            Beauty Emergency Clinic
          </Typography>
          {user && (
            <Typography variant="body2" color="text.secondary">
              {user.username}
            </Typography>
          )}
        </Box>

        <List sx={{ p: 1 }}>
          <ListItem 
            button 
            component={Link} 
            to="/" 
            onClick={toggleDrawer(false)}
            sx={{ 
              borderRadius: '8px', 
              mb: 0.5,
              ...(location.pathname === '/' && {
                bgcolor: 'rgba(2, 102, 204, 0.08)',
              }),
              '&:hover': {
                bgcolor: 'rgba(2, 102, 204, 0.05)',
              }
            }}
          >
            <ListItemIcon>
              <HomeIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="Dashboard" />
          </ListItem>


          <ListItem 
            button 
            component={Link} 
            to="/documents" 
            onClick={toggleDrawer(false)}
            sx={{ 
              borderRadius: '8px', 
              mb: 0.5,
              ...(location.pathname === '/documents' && {
                bgcolor: 'rgba(2, 102, 204, 0.08)',
              }),
              '&:hover': {
                bgcolor: 'rgba(2, 102, 204, 0.05)',
              }
            }}
          >
            <ListItemIcon>
              <FolderSharedIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="Documents" />
          </ListItem>

          <ListItem 
            button 
            component={Link} 
            to="/chat" 
            onClick={toggleDrawer(false)}
            sx={{ 
              borderRadius: '8px', 
              mb: 0.5,
              ...(location.pathname === '/chat' && {
                bgcolor: 'rgba(2, 102, 204, 0.08)',
              }),
              '&:hover': {
                bgcolor: 'rgba(2, 102, 204, 0.05)',
              }
            }}
          >
            <ListItemIcon>
              <ChatIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="Chat" />
            <ChatNotificationBadge />
          </ListItem>
          
          <ListItem 
            button 
            component={Link} 
            to="/hydra-chat" 
            onClick={toggleDrawer(false)}
            sx={{ 
              borderRadius: '8px', 
              mb: 0.5,
              ...(location.pathname === '/hydra-chat' && {
                bgcolor: 'rgba(2, 102, 204, 0.08)',
              }),
              '&:hover': {
                bgcolor: 'rgba(2, 102, 204, 0.05)',
              }
            }}
          >
            <ListItemIcon>
              <SmartToyIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="AI Chat" />
          </ListItem>
          
          <ListItem 
            button 
            component={Link} 
            to="/request-center" 
            onClick={toggleDrawer(false)}
            sx={{ 
              borderRadius: '8px', 
              mb: 0.5,
              ...(location.pathname === '/request-center' && {
                bgcolor: 'rgba(2, 102, 204, 0.08)',
              }),
              '&:hover': {
                bgcolor: 'rgba(2, 102, 204, 0.05)',
              }
            }}
          >
            <ListItemIcon>
              <DescriptionIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="Request Center" />
          </ListItem>

          <Divider sx={{ my: 1.5, borderColor: 'rgba(86, 106, 135, 0.12)' }} />

          {user && (
            <ListItem 
              button 
              onClick={handleChangePassword}
              sx={{ 
                borderRadius: '8px', 
                mb: 0.5,
                '&:hover': {
                  bgcolor: 'rgba(2, 102, 204, 0.05)',
                }
              }}
            >
              <ListItemIcon>
                <LockIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="Change Password" />
            </ListItem>
          )}

          {user && user.role === 'admin' && (
            <ListItem 
              button 
              onClick={handleUserManagement}
              sx={{ 
                borderRadius: '8px', 
                mb: 0.5,
                '&:hover': {
                  bgcolor: 'rgba(2, 102, 204, 0.05)',
                }
              }}
            >
              <ListItemIcon>
                <AdminPanelSettingsIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="User Management" />
            </ListItem>
          )}

          <Divider sx={{ my: 1.5, borderColor: 'rgba(86, 106, 135, 0.12)' }} />

          <ListItem 
            button 
            onClick={handleLogout}
            sx={{ 
              borderRadius: '8px',
              '&:hover': {
                bgcolor: 'rgba(230, 57, 70, 0.05)',
              }
            }}
          >
            <ListItemIcon>
              <LogoutIcon sx={{ color: '#e63946' }} />
            </ListItemIcon>
            <ListItemText primary="Logout" sx={{ color: '#e63946' }} />
          </ListItem>
        </List>
      </Drawer>

      <ChangePasswordDialog
        open={passwordDialogOpen}
        onClose={() => setPasswordDialogOpen(false)}
        onSuccess={handlePasswordSuccess}
      />
    </>
  );
};

export default NavBar;